import React from 'react';
import {render, unmountComponentAtNode} from 'react-dom';
import {Provider} from 'react-redux';
import {bindActionCreators} from 'redux';
import {getDOMNode, getOptionsFromDataAttrs, getOptionsFromQueryString, range} from 'utils';
import StopWatch from 'utils/stopwatch';
import {loadTheme} from 'themes';
import {findVideos, getAdapter} from 'adapters';
import {initTranslations} from 'locale';
import {configureStore} from 'store';
import {parseOptions, setLanguage, initialRender, initReady} from 'store/config/actions';
import * as videoActions from 'store/video/actions';
import {loadManifest as loadElementListManifest, loadElementList} from 'store/element-list/actions';
import {loadManifest as loadCaptionsManifest} from 'store/captions/actions';
import Root from 'components/layout/root';
import {buildTranscript} from './export';

const ID_ATTR = `data-${VWRAP.cssNamespace}-id`;
const ORIGINAL_STYLE_ATTR = `data-${VWRAP.cssNamespace}-original-style`;

export const getWrapperId = ({provider, videoId}) => `${VWRAP.cssNamespace}-${provider}-${videoId}`;
export const getWrapperNode = wrapperId => getDOMNode(`[${ID_ATTR}="${wrapperId}"]`);

const createMatadataGetter = (state, adapter) => async (videoMetadata) => {
    const metadata = (videoMetadata || await adapter.getMetadata());
    const transcript = buildTranscript(state.elementList, name);
    return {...metadata, transcript};
};

const getContainer = ({provider, videoId, videoNode, domContainer, theme}) => {
    let container;

    if (domContainer) {
        container = getDOMNode(domContainer);
    } else {
        container = document.createElement('div');
        videoNode.parentNode.insertBefore(container, videoNode.nextSibling);
    }

    if (!container) return;

    const id = getWrapperId({provider, videoId});
    if (!container.id) container.id = id;

    container.setAttribute(ID_ATTR, id);
    container.classList.add(VWRAP.cssNamespace);
    container.classList.add(`${VWRAP.cssNamespace}-${theme}`);

    return container;
};

export const destroyWrapper = (containerNode, options) => {
    unmountComponentAtNode(containerNode);

    if (options.domContainer) { // Cleanup without removing the container so it's available for future uses.
        containerNode.classList.forEach(cls => {
            if (cls.startsWith(VWRAP.cssNamespace)) containerNode.classList.remove(cls);
        });
    } else {
        // Reset video node's styles to the original ones, the wrapper node will be removed so we don't need to care
        // about it. The video node should be right "before" the container node.
        const originalStyle = JSON.parse(containerNode.getAttribute(ORIGINAL_STYLE_ATTR) || false);
        const videoNode = originalStyle && containerNode.previousSibling;
        if (videoNode) {
            videoNode.style.setProperty('margin-top', 0);
            Object.keys(originalStyle).forEach(key => {
                videoNode.style.setProperty(key, originalStyle[key]);
            });
        }
        containerNode.remove();
    }
};

const isInitialized = ({provider, videoId}) => {
    const wrapperId = getWrapperId({provider, videoId});
    return Boolean(getWrapperNode(wrapperId));
};

const saveVideoOriginalStyle = (containerNode, videoNode) => {
    const style = range(videoNode.style.length)
        .reduce((result, index) => {
            const property = videoNode.style.item(index);
            const value = videoNode.style.getPropertyValue(property);
            result[property] = value;
            return result;
        }, {});
    containerNode.setAttribute(ORIGINAL_STYLE_ATTR, JSON.stringify(style));
};

export const initWrapper = async (options) => {
    const stopwatch = new StopWatch('Initialization');
    options.wrapperId = getWrapperId(options);

    // Merge options from querystring
    const rootProps = {};
    const qsOptions = getOptionsFromQueryString(window.location.search);

    if (options.wrapperId === qsOptions.wrapperId) {
        Object.assign(options, qsOptions);
        rootProps.scrollIntoView = true;
    }
    stopwatch.lap('Parsed querystring');

    // The adapter is the bridge between the wrapper and the video player
    const adapter = getAdapter(options.provider);
    stopwatch.lap('Created adapter');

    // Create redux store
    const store = configureStore(adapter, {debug: VWRAP.debug || options.debugMessages});
    stopwatch.lap('Created redux store');

    // Initialize the adapter
    stopwatch.lap('Started adapter initialization');
    await adapter.initialize({
        ...options,
        ...bindActionCreators(videoActions, store.dispatch),
    });
    stopwatch.lap('Ended adapter initialization');

    // Add adapter options
    Object.assign(options, adapter.getOptions());

    // Parse and save the options in the store
    await store.dispatch(parseOptions(options));
    const {config} = store.getState();
    stopwatch.lap('Parsed options');

    if (config.get('disabled')) return;

    // Get the container for the wrapper using adapter.videoNode instead of options.videoNode
    // because the adapter might have updated the reference to another DOM node
    const theme = config.get('theme');
    const domContainer = config.get('domContainer');
    const container = getContainer({
        provider: adapter.provider,
        videoId: adapter.videoId,
        videoNode: adapter.videoNode,
        domContainer,
        theme,
    });
    if (!container) return;

    // Save the video's original styling
    if (!domContainer) saveVideoOriginalStyle(container, adapter.videoNode);

    stopwatch.lap('Configured container');

    // Load manifest (contains URLs for the available ElementLists)
    await store.dispatch(loadElementListManifest());
    stopwatch.lap('Loaded manifest');

    // Load manifest (contains URLs for the available Captions)
    await store.dispatch(loadCaptionsManifest());
    stopwatch.lap('Load captions');

    await loadTheme(theme);
    stopwatch.lap('Loaded theme');

    // Initialize language
    const activeLanguage = await initTranslations(config.get('language'));
    store.dispatch(setLanguage(activeLanguage));
    stopwatch.lap('Initialized language');

    // Render!
    render(
        <Provider store={store}>
            <Root videoNode={adapter.videoNode} {...rootProps} />
        </Provider>,
        container,
    );
    store.dispatch(initialRender());
    stopwatch.lap('First render');

    await store.dispatch(loadElementList());
    stopwatch.lap('Loaded ElementList');

    // Wait for adapter to report video duration before loading the ElementList
    await store.wait(state => state.video.get('duration'));
    stopwatch.lap('Waited for adapter to report video duration');

    // Final stuff after everything is loaded / rendered
    store.dispatch(videoActions.seekFromConfig());
    store.dispatch(initReady());
    stopwatch.lap('Final actions');

    if (VWRAP.debug || options.debugPerformance) stopwatch.print();

    return createMatadataGetter(store.getState(), adapter);
};

const scriptOptions = document.currentScript ? getOptionsFromDataAttrs(document.currentScript) : {};

export const cieloWrap = (overrides = {}) => (
    findVideos()
        .filter(video => !isInitialized(video))
        .map(video => {
            const embedOptions = getOptionsFromDataAttrs(video.entryNode || video.videoNode);
            return exports.initWrapper(Object.assign({}, video, scriptOptions, embedOptions, overrides));
        })
);



// WEBPACK FOOTER //
// ./scripts/init.js